import { listTransactionCustomColumns } from './transaction-custom-column-value.js';

// #6275/#6405: shared by the Ledgers and Events & Transactions tables and their inline editors, so every
// custom column is edited and shown according to its format.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A custom column's format: 'text' (the default), 'currency', 'number' or 'date'. */
export function customColumnFormat(clientData, colKey) {
	const col = listTransactionCustomColumns(clientData).find((c) => c.key === colKey);
	return col?.format || 'text';
}

function toNumber(raw) {
	const cleaned = String(raw ?? '').replace(/[$,\s]/g, '');
	return cleaned !== '' && Number.isFinite(Number(cleaned)) ? cleaned : null;
}

/** 'YYYY-MM-DD' for 2026-01-15, 1/15/2026 or 1/15/26, or null if it isn't a real date. */
export function parseCustomDate(raw) {
	const text = String(raw ?? '').trim();
	let y, m, d;
	let match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
	if (match) [, y, m, d] = match.map(Number);
	else if ((match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/))) {
		[, m, d, y] = match.map(Number);
		if (y < 100) y += 2000;
	} else return null;
	const date = new Date(Date.UTC(y, m - 1, d));
	if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
	return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Value to save from an inline edit, or null to revert. Empty clears the value in every format. */
export function parseInlineCustomValue(format, raw) {
	const text = String(raw ?? '').trim();
	if (format === 'currency' || format === 'number') {
		if (text.replace(/\$/g, '').trim() === '') return '';
		return toNumber(text);
	}
	if (format === 'date') return text === '' ? '' : parseCustomDate(text);
	return text;
}

/** Cell text for a stored value. A value that doesn't fit the format (e.g. imported as typed) is shown as-is. */
export function formatCustomColumnValue(format, raw, currencyFormat) {
	const text = String(raw ?? '').trim();
	if (format === 'currency') {
		if (text === '') return 'n.a.';
		const num = toNumber(text);
		return num === null ? text : Number(num).toLocaleString('en-US', currencyFormat);
	}
	if (format === 'number') {
		const num = toNumber(text);
		return num === null ? text : Number(num).toLocaleString('en-US', { maximumFractionDigits: 6 });
	}
	if (format === 'date') {
		const iso = parseCustomDate(text);
		if (!iso) return text;
		const [y, m, d] = iso.split('-').map(Number);
		return `${MONTHS[m - 1]} ${d}, ${y}`;
	}
	return text;
}

/** For values and labels written into table HTML. */
export function escapeCustomColumnText(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * #6405: value to store from an imported cell. Currency/number cells lose `$`, commas and spaces
 * (so a Ledgers export re-imports cleanly), but anything that isn't then a number is kept as typed.
 */
export function parseImportedCustomValue(format, raw) {
	const text = String(raw ?? '').trim();
	if (format !== 'currency' && format !== 'number') return text;
	if (text.toLowerCase() === 'n.a.') return '';
	const cleaned = text.replace(/[$,\s]/g, '');
	return cleaned !== '' && !isNaN(Number(cleaned)) ? cleaned : text;
}
